import { addDays, addMonths, addWeeks, startOfMonth } from "date-fns";
import { isEqual } from "lodash";

import CodeUtil from "@plannel/pages/utils/code-util";
import dateUtils from "@plannel/utils/date-util";

const { buckets, module: moduleCode, dayOfWeek } = CodeUtil;
const { DAY, PARTIAL_WEEK, WEEK, MONTH } = buckets;
const { MP } = moduleCode;

const MONDAY_CD = dayOfWeek.MONDAY.cd;

/**
 * 버킷 유형 문자열을 소문자로 정규화한다.
 * 저장된 값의 대소문자가 일정하지 않아 비교 전에 반드시 거쳐야 한다.
 */
export const normalizeBucketUnit = bucket => {
  if (!bucket) {
    return null;
  }
  return bucket.toString().toLowerCase();
};

/**
 * 계획 설정에서 대표(첫 번째) 버킷 유형을 구한다.
 * MP 는 단일 BUCKET_UNIT 이 아니라 TIME_BUCKETS 배열을 쓰므로 그쪽을 본다.
 * 어느 쪽도 없으면 null — 레거시 BUCKET_UNIT 폴백이 필요한 화면은 호출부에서 처리한다.
 */
export const getPrimaryBucketUnit = (planConfigObj) => {
  if (!planConfigObj) {
    return null;
  }

  if (Array.isArray(planConfigObj.TIME_BUCKETS) && planConfigObj.TIME_BUCKETS.length > 0) {
    return normalizeBucketUnit(planConfigObj.TIME_BUCKETS[0]?.UNIT);
  }

  if (Array.isArray(planConfigObj.TIME_BUCKET_SEGMENTS) && planConfigObj.TIME_BUCKET_SEGMENTS.length > 0) {
    return normalizeBucketUnit(planConfigObj.TIME_BUCKET_SEGMENTS[0]?.bucket);
  }

  return null;
};

/**
 * 계획 설정에서 마지막 버킷 유형을 구한다.
 * MP 는 세그먼트마다 버킷이 달라(예: 앞은 주, 뒤는 월) 종료일 표기는 이 값을 따라야 한다.
 * 세그먼트 정보가 없으면 대표 버킷으로 폴백한다.
 */
export const getLastBucketUnit = (planConfigObj) => {
  if (!planConfigObj) {
    return null;
  }

  if (Array.isArray(planConfigObj.TIME_BUCKETS) && planConfigObj.TIME_BUCKETS.length > 0) {
    return normalizeBucketUnit(planConfigObj.TIME_BUCKETS[planConfigObj.TIME_BUCKETS.length - 1]?.UNIT);
  }

  if (Array.isArray(planConfigObj.TIME_BUCKET_SEGMENTS) && planConfigObj.TIME_BUCKET_SEGMENTS.length > 0) {
    return normalizeBucketUnit(planConfigObj.TIME_BUCKET_SEGMENTS[planConfigObj.TIME_BUCKET_SEGMENTS.length - 1]?.bucket);
  }

  return getPrimaryBucketUnit(planConfigObj);
};

/**
 * 버킷 유형별 기본 버킷 수(계획 기간).
 * 버킷 유형 변경 시 자동 산출 기준값으로 사용한다.
 */
export const DEFAULT_RANGE_BY_BUCKET = {
  [DAY]: 90,
  [PARTIAL_WEEK]: 12,
  [WEEK]: 12,
  [MONTH]: 3,
};

/**
 * 버킷 유형이 변경될 때 자동 적용할 기본 버킷 수를 반환한다.
 * 알 수 없는 유형이면 null.
 */
export function getDefaultRangeByBucketUnit(bucketUnit) {
  return DEFAULT_RANGE_BY_BUCKET[bucketUnit] ?? null;
}

/**
 * 시작일 + 버킷 유형 + 버킷 수로 종료일(inclusive)을 계산한다.
 * 종료일 = 시작일로부터 count 버킷 이후 - 1일
 * 알 수 없는 UOM이거나 필수 인자가 없으면 null.
 */
export function calcEndDateByUom(startDate, uom, count, startDay = MONDAY_CD) {
  if (!startDate || count == null) return null;

  let endDate;
  if (isEqual(uom, DAY)) {
    endDate = addDays(startDate, count);
  } else if (isEqual(uom, PARTIAL_WEEK)) {
    endDate = dateUtils.calculateRangeDate(startDate, PARTIAL_WEEK, startDay, count, false);
  } else if (isEqual(uom, WEEK)) {
    endDate = addWeeks(startDate, count);
  } else if (isEqual(uom, MONTH)) {
    endDate = addMonths(startDate, count);
  } else {
    return null;
  }

  return addDays(endDate, -1);
}

/**
 * MP 가변 타임버킷(TIME_BUCKETS) 배열로 종료일을 계산한다.
 * 각 버킷 세그먼트를 순차 연결하여 최종 종료일을 반환한다.
 */
export function calcEndDateByTimeBuckets(startDate, tbs, startDay = MONDAY_CD) {
  if (!startDate || !Array.isArray(tbs) || tbs.length === 0) return null;

  let curDate = new ZDate(startDate);
  let endDate = new ZDate(startDate);

  for (const tb of tbs) {
    const count = tb.COUNT;
    if (count > 0) {
      if (isEqual(tb.UNIT, MONTH)) curDate = new ZDate(startOfMonth(curDate));
      const segEnd = calcEndDateByUom(curDate, tb.UNIT, count, startDay);
      if (!segEnd) return null;
      endDate = segEnd;
      curDate = addDays(new ZDate(endDate), 1);
    }
  }

  return endDate;
}

/**
 * 모듈 유형에 따라 종료일을 계산한다.
 * - MP: TIME_BUCKETS 배열 기반 계산 (calcEndDateByTimeBuckets)
 * - 그 외: 단일 버킷 유형 + 버킷 수 기반 계산 (calcEndDateByUom)
 */
export function calcPlanEndDate(startDate, range, unit, tbs, moduleCd, startDay = MONDAY_CD) {
  if (!startDate) return null;

  if (isEqual(moduleCd, MP)) {
    return calcEndDateByTimeBuckets(startDate, tbs, startDay);
  }

  return calcEndDateByUom(startDate, unit, range, startDay);
}

// ─── 날짜 경계 헬퍼 ───────────────────────────────────────────────────────────

export const getNextMonday = (date) => {
  if (!date) return date;
  const d = new ZDate(date);
  const day = d.getDay();
  if (day === 1) return d;
  return addDays(d, day === 0 ? 1 : 8 - day);
};

export const getNextFirstOfMonth = (date) => {
  if (!date) return date;
  const d = new ZDate(date);
  if (d.getDate() === 1) return d;
  return new ZDate(startOfMonth(addMonths(d, 1)));
};

// ─── 버킷 진행 규칙 ───────────────────────────────────────────────────────────

export const ALL_MP_BUCKET_OPTIONS = [DAY, WEEK, PARTIAL_WEEK, MONTH];

export const getAllowedBucketsByPrevious = (previousBucket) => {
  if (previousBucket === DAY) return [WEEK, PARTIAL_WEEK, MONTH];
  if (previousBucket === WEEK || previousBucket === PARTIAL_WEEK || previousBucket === MONTH) return [MONTH];
  return [MONTH];
};

export const getAllowedBucketsByNext = (nextBucket) => {
  if (nextBucket === WEEK || nextBucket === PARTIAL_WEEK) return [DAY];
  if (nextBucket === MONTH) return [DAY, WEEK, PARTIAL_WEEK];
  return ALL_MP_BUCKET_OPTIONS;
};

export const convertBucketCount = (fromUnit, toUnit, count) => {
  if (fromUnit === toUnit) return count;
  const days =
    fromUnit === DAY ? count :
    (fromUnit === WEEK || fromUnit === PARTIAL_WEEK) ? count * 7 :
    fromUnit === MONTH ? count * 30 : count;
  if (toUnit === DAY) return Math.max(1, days);
  if (toUnit === WEEK || toUnit === PARTIAL_WEEK) return Math.max(1, Math.ceil(days / 7));
  if (toUnit === MONTH) return Math.max(1, Math.ceil(days / 30));
  return Math.max(1, count);
};

export const enforceTimeBucketProgression = (tbs) => {
  if (!Array.isArray(tbs) || tbs.length === 0) {
    return [{ UNIT: DAY, COUNT: 1 }];
  }
  const result = [];
  for (const tb of tbs) {
    const count = Math.max(1, Number(tb.COUNT) || 1);
    if (result.length === 0) {
      result.push({ UNIT: ALL_MP_BUCKET_OPTIONS.includes(tb.UNIT) ? tb.UNIT : DAY, COUNT: count });
    } else {
      const prevUnit = result[result.length - 1].UNIT;
      const allowed = getAllowedBucketsByPrevious(prevUnit);
      result.push({ UNIT: allowed.includes(tb.UNIT) ? tb.UNIT : allowed[0], COUNT: count });
    }
  }
  return result;
};

// ─── 세그먼트 날짜 계산 ────────────────────────────────────────────────────────

export const calcSegmentStartDatesWithBoundary = (planStart, tbs) => {
  if (!planStart || !Array.isArray(tbs)) return (tbs ?? []).map(() => null);
  const dates = [];
  let cur = new ZDate(planStart);
  for (const tb of tbs) {
    const segStart = tb.UNIT === MONTH ? new ZDate(startOfMonth(cur)) : new ZDate(cur);
    dates.push(segStart);
    const count = Math.max(1, tb.COUNT || 1);
    if (tb.UNIT === DAY) {
      cur = addDays(segStart, count);
    } else if (tb.UNIT === WEEK) {
      cur = addDays(segStart, count * 7);
    } else if (tb.UNIT === PARTIAL_WEEK) {
      let next = new ZDate(segStart);
      for (let i = 0; i < count; i++) {
        const plusWeek = addDays(next, 7);
        if (next.getMonth() !== plusWeek.getMonth()) {
          next = new ZDate(startOfMonth(plusWeek));
        } else if (next.getDay() === 1) {
          next = new ZDate(plusWeek);
        } else {
          next = getNextMonday(next);
        }
      }
      cur = next;
    } else if (tb.UNIT === MONTH) {
      cur = addMonths(segStart, count);
    }
  }
  return dates;
};

export const calcWeekCountToNextMonth = (weekStart) => {
  const monthStart = new ZDate(startOfMonth(addMonths(weekStart, 1)));
  const diffDays = Math.round((monthStart - weekStart) / 86400000);
  return Math.max(1, Math.ceil(diffDays / 7));
};

export const calcWeekCountBeforeMonth = (weekStart, count) => {
  const lastWeekStart = addDays(weekStart, (Math.max(1, count || 1) - 1) * 7);
  const monthStart = new ZDate(startOfMonth(addMonths(lastWeekStart, 1)));
  const diffDays = Math.round((monthStart - weekStart) / 86400000);
  return Math.max(1, Math.ceil(diffDays / 7));
};

export const calcPartialWeekCountBeforeMonth = (start, count) => {
  let lastStart = new ZDate(start);
  for (let i = 1; i < Math.max(1, count || 1); i++) {
    const plusWeek = addDays(lastStart, 7);
    if (lastStart.getMonth() !== plusWeek.getMonth()) {
      lastStart = new ZDate(startOfMonth(plusWeek));
    } else if (lastStart.getDay() === 1) {
      lastStart = new ZDate(plusWeek);
    } else {
      lastStart = getNextMonday(lastStart);
    }
  }
  return calcPartialWeekCountToDate(start, new ZDate(startOfMonth(addMonths(lastStart, 1))));
};

export const calcPartialWeekCountToDate = (startDate, targetDate) => {
  let cur = new ZDate(startDate);
  let count = 0;
  while (cur < targetDate && count < 1000) {
    const plusWeek = addDays(cur, 7);
    if (cur.getMonth() !== plusWeek.getMonth()) {
      cur = new ZDate(startOfMonth(plusWeek));
    } else if (cur.getDay() === 1) {
      cur = new ZDate(plusWeek);
    } else {
      cur = getNextMonday(cur);
    }
    count++;
  }
  return Math.max(1, count);
};

export const initializeTimeBuckets = (planStartDate, tbs) => {
  if (!planStartDate || !Array.isArray(tbs) || tbs.length === 0) {
    return { newPlanStart: planStartDate ? new ZDate(planStartDate) : null, newTbs: tbs ?? [] };
  }

  const cur = enforceTimeBucketProgression(tbs).map(tb => ({ ...tb }));
  let newPlanStart = new ZDate(planStartDate);
  let segStart = new ZDate(planStartDate);
  let prevStart = null;

  for (let i = 0; i < cur.length; i++) {
    const tb = cur[i];
    let snappedStart = new ZDate(segStart);

    if (tb.UNIT === WEEK || tb.UNIT === PARTIAL_WEEK) {
      snappedStart = getNextMonday(segStart);
    } else if (tb.UNIT === MONTH) {
      snappedStart = i > 0 && cur[i - 1].UNIT === WEEK
        ? new ZDate(startOfMonth(segStart))
        : getNextFirstOfMonth(segStart);
    }

    const diffMs = snappedStart - segStart;
    if (diffMs > 0) {
      if (i === 0) {
        newPlanStart = new ZDate(snappedStart);
      } else {
        const prevTb = cur[i - 1];
        if (prevTb.UNIT === DAY) {
          cur[i - 1].COUNT = Math.max(1, Math.round((snappedStart - prevStart) / 86400000));
        }
      }
    }

    if (tb.UNIT === WEEK && cur[i + 1]?.UNIT === MONTH) {
      cur[i].COUNT = calcWeekCountBeforeMonth(snappedStart, cur[i].COUNT);
    } else if (tb.UNIT === PARTIAL_WEEK && cur[i + 1]?.UNIT === MONTH) {
      cur[i].COUNT = calcPartialWeekCountBeforeMonth(snappedStart, cur[i].COUNT);
    }

    prevStart = new ZDate(snappedStart);
    const count = cur[i].COUNT;

    if (tb.UNIT === DAY) {
      segStart = addDays(snappedStart, count);
    } else if (tb.UNIT === WEEK) {
      segStart = addDays(snappedStart, count * 7);
    } else if (tb.UNIT === PARTIAL_WEEK) {
      let next = new ZDate(snappedStart);
      for (let j = 0; j < count; j++) {
        const plusWeek = addDays(next, 7);
        if (next.getMonth() !== plusWeek.getMonth()) {
          next = new ZDate(startOfMonth(plusWeek));
        } else if (next.getDay() === 1) {
          next = new ZDate(plusWeek);
        } else {
          next = getNextMonday(next);
        }
      }
      segStart = next;
    } else if (tb.UNIT === MONTH) {
      segStart = addMonths(snappedStart, count);
    } else {
      segStart = addDays(snappedStart, count);
    }
  }

  return { newPlanStart, newTbs: enforceTimeBucketProgression(cur) };
};